﻿import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { FiEdit, FiPrinter, FiMail, FiDownload, FiTrash2, FiCreditCard, FiDollarSign } from 'react-icons/fi';

interface InvoiceItem {
        id: string;
        item_number: string;
        date_of_service: string;
        quantity: number;
        description: string;
        fee: number;
        gst_applicable: boolean;
        notes?: string;
}

interface Payment {
        id: string;
        payment_type: string;
        amount: number;
        payment_date: string;
        reference?: string;
}

interface Invoice {
        id: string;
        invoice_number: string;
        invoice_date: string;
        patient: {
                id: number;
                first_name: string;
                last_name: string;
                medical_record_number?: string;
                date_of_birth?: string;
        };
        doctor: {
                id: number;
                first_name: string;
                last_name: string;
                provider_number?: string;
        };
        facility: {
                id: number;
                name: string;
                address?: string;
                phone?: string;
        };
        account_type: string;
        patient_type: string;
        invoice_type: string;
        account_holder_info?: string;
        admission_date?: string;
        discharge_date?: string;
        location_type: string;
        duration?: string;
        referral_reason?: string;
        items: InvoiceItem[];
        payments: Payment[];
        subtotal: number;
        gst: number;
        discount: number;
        total_rebate: number;
        total: number;
        amount_paid: number;
        balance: number;
        status: 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled';
        notes?: string;
}

const InvoiceView: React.FC = () => {
        const { id } = useParams<{ id: string }>();
        const navigate = useNavigate();
        const { token } = useAuth();
        const [invoice, setInvoice] = useState<Invoice | null>(null);
        const [loading, setLoading] = useState(true);
        const [showPaymentModal, setShowPaymentModal] = useState(false);
        const [paymentAmount, setPaymentAmount] = useState('');
        const [paymentType, setPaymentType] = useState('cash');
        const [paymentReference, setPaymentReference] = useState('');

        useEffect(() => {
                if (id && token) {
                        fetchInvoice();
                }
        }, [id, token]);

        const fetchInvoice = async () => {
                try {
                        const response = await apiCall(`/billing/invoices/${id}/`, { method: 'GET' }, token!);
                        if (response.ok) {
                                const data = await response.json();
                                setInvoice(data);
                        }
                } catch (error) {
                        console.error('Error fetching invoice:', error);
                } finally {
                        setLoading(false);
                }
        };

        const handlePrint = () => {
                window.print();
        };

        const handleEmail = async () => {
                if (!invoice) return;
                // TODO: Implement email invoice
                alert('Email invoice functionality - to be implemented');
        };

        const handleDownload = async () => {
                if (!invoice) return;
                // TODO: Implement PDF download
                alert('Download PDF functionality - to be implemented');
        };

        const handleAddPayment = async () => {
                if (!invoice || !paymentAmount) return;

                try {
                        const response = await apiCall(
                                `/billing/invoices/${invoice.id}/payments/`,
                                {
                                        method: 'POST',
                                        body: JSON.stringify({
                                                payment_type: paymentType,
                                                amount: parseFloat(paymentAmount),
                                                reference: paymentReference,
                                        }),
                                },
                                token!
                        );

                        if (response.ok) {
                                setShowPaymentModal(false);
                                setPaymentAmount('');
                                setPaymentReference('');
                                fetchInvoice(); // Refresh invoice
                        }
                } catch (error) {
                        console.error('Error adding payment:', error);
                        alert('Failed to add payment');
                }
        };

        const getStatusBadgeClass = (status: Invoice['status']) => {
                const baseClass = 'badge badge-lg';
                switch (status) {
                        case 'paid':
                                return `${baseClass} badge-success`;
                        case 'partial':
                                return `${baseClass} badge-warning`;
                        case 'overdue':
                                return `${baseClass} badge-error`;
                        case 'sent':
                                return `${baseClass} badge-info`;
                        case 'cancelled':
                                return `${baseClass} badge-ghost`;
                        default:
                                return `${baseClass} badge-ghost`;
                }
        };

        if (loading) {
                return (
                        <div className="flex items-center justify-center h-screen">
                                <div className="loading loading-spinner loading-lg"></div>
                        </div>
                );
        }

        if (!invoice) {
                return (
                        <div className="p-8 text-center">
                                <p className="text-error">Invoice not found</p>
                                <button onClick={() => navigate('/billing/invoices')} className="btn btn-primary mt-4">
                                        Back to Invoices
                                </button>
                        </div>
                );
        }

        return (
                <div className="p-6 max-w-7xl mx-auto">
                        {/* Header Actions */}
                        <div className="flex justify-between items-center mb-6 print:hidden">
                                <button onClick={() => navigate('/billing/invoices')} className="btn btn-ghost">
                                        ← Back to Invoices
                                </button>
                                <div className="flex gap-2">
                                        <button onClick={() => setShowPaymentModal(true)} className="btn btn-success gap-2">
                                                <FiDollarSign /> Add Payment
                                        </button>
                                        <button onClick={() => navigate(`/billing/invoices/edit/${invoice.id}`)} className="btn btn-primary gap-2">
                                                <FiEdit /> Edit
                                        </button>
                                        <button onClick={handleEmail} className="btn btn-ghost gap-2">
                                                <FiMail /> Email
                                        </button>
                                        <button onClick={handleDownload} className="btn btn-ghost gap-2">
                                                <FiDownload /> Download
                                        </button>
                                        <button onClick={handlePrint} className="btn btn-ghost gap-2">
                                                <FiPrinter /> Print
                                        </button>
                                </div>
                        </div>

                        {/* Invoice Content */}
                        <div className="bg-base-100 rounded-lg shadow-lg p-8">
                                {/* Invoice Header */}
                                <div className="flex justify-between items-start mb-8">
                                        <div>
                                                <h1 className="text-2xl font-bold mb-2">Invoice</h1>
                                                <div className="flex items-center gap-4">
                                                        <p className="text-lg">#{invoice.invoice_number}</p>
                                                        <span className={getStatusBadgeClass(invoice.status)}>
                                                                {invoice.status.toUpperCase()}
                                                        </span>
                                                </div>
                                        </div>
                                        <div className="text-right">
                                                <h2 className="text-xl font-semibold">{invoice.facility.name}</h2>
                                                {invoice.facility.address && <p className="text-sm opacity-70">{invoice.facility.address}</p>}
                                                {invoice.facility.phone && <p className="text-sm opacity-70">Phone: {invoice.facility.phone}</p>}
                                        </div>
                                </div>

                                {/* Patient and Doctor Info */}
                                <div className="grid grid-cols-2 gap-8 mb-8">
                                        <div>
                                                <h3 className="font-semibold mb-2">Patient Information</h3>
                                                <p className="font-medium text-lg">
                                                        {invoice.patient.first_name} {invoice.patient.last_name}
                                                </p>
                                                <p className="text-sm opacity-70">Account Type: {invoice.account_type}</p>
                                                {invoice.patient.date_of_birth && (
                                                        <p className="text-sm opacity-70">DOB: {invoice.patient.date_of_birth}</p>
                                                )}
                                                {invoice.patient.medical_record_number && (
                                                        <p className="text-sm opacity-70">MRN: {invoice.patient.medical_record_number}</p>
                                                )}
                                                {invoice.account_holder_info && (
                                                        <div className="mt-2 p-2 bg-base-200 rounded">
                                                                <p className="text-sm font-medium">Account Holder:</p>
                                                                <p className="text-sm opacity-70">{invoice.account_holder_info}</p>
                                                        </div>
                                                )}
                                        </div>
                                        <div>
                                                <h3 className="font-semibold mb-2">Invoice Details</h3>
                                                <div className="space-y-1">
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Invoice No:</span>{' '}
                                                                <span className="font-medium">{invoice.invoice_number}</span>
                                                        </p>
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Invoice Date:</span>{' '}
                                                                <span className="font-medium">{invoice.invoice_date}</span>
                                                        </p>
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Doctor:</span>{' '}
                                                                <span className="font-medium">
                                                                        Dr {invoice.doctor.first_name} {invoice.doctor.last_name}
                                                                </span>
                                                        </p>
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Location:</span>{' '}
                                                                <span className="font-medium">{invoice.facility.name}</span>
                                                        </p>
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Patient Type:</span>{' '}
                                                                <span className="font-medium">{invoice.patient_type}</span>
                                                        </p>
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Type of Invoice:</span>{' '}
                                                                <span className="font-medium">{invoice.invoice_type}</span>
                                                        </p>
                                                        {invoice.duration && (
                                                                <p className="text-sm">
                                                                        <span className="opacity-70">Duration:</span>{' '}
                                                                        <span className="font-medium">{invoice.duration}</span>
                                                                </p>
                                                        )}
                                                        {invoice.referral_reason && (
                                                                <p className="text-sm">
                                                                        <span className="opacity-70">Referral Reason:</span>{' '}
                                                                        <span className="font-medium">{invoice.referral_reason}</span>
                                                                </p>
                                                        )}
                                                        <p className="text-sm">
                                                                <span className="opacity-70">Location Type:</span>{' '}
                                                                <span className="font-medium">{invoice.location_type}</span>
                                                        </p>
                                                </div>
                                        </div>
                                </div>

                                {/* Items Table */}
                                <div className="mb-8">
                                        <h3 className="font-semibold mb-4">Item Details</h3>
                                        <div className="overflow-x-auto">
                                                <table className="table w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>Item Number</th>
                                                                        <th>Date of Service</th>
                                                                        <th>Quantity</th>
                                                                        <th>Description</th>
                                                                        <th className="text-right">Fees</th>
                                                                        <th className="text-center">Notes</th>
                                                                        <th className="text-center">GST</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {invoice.items.map((item) => (
                                                                        <tr key={item.id}>
                                                                                <td>{item.item_number}</td>
                                                                                <td>{item.date_of_service}</td>
                                                                                <td>{item.quantity}</td>
                                                                                <td>{item.description}</td>
                                                                                <td className="text-right">${item.fee.toFixed(2)}</td>
                                                                                <td className="text-center">{item.notes || '-'}</td>
                                                                                <td className="text-center">
                                                                                        {item.gst_applicable ? '✓' : '-'}
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>
                                </div>

                                {/* Totals */}
                                <div className="flex justify-end mb-8">
                                        <div className="w-full max-w-md">
                                                <div className="bg-base-200 rounded-lg p-4 space-y-2">
                                                        <div className="flex justify-between">
                                                                <span>Total:</span>
                                                                <span className="font-medium">${invoice.subtotal.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                                <span>GST:</span>
                                                                <span className="font-medium">${invoice.gst.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                                <span>Discount:</span>
                                                                <span className="font-medium">${invoice.discount.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                                <span>Total Rebate:</span>
                                                                <span className="font-medium">${invoice.total_rebate.toFixed(2)}</span>
                                                        </div>
                                                        <div className="divider my-2"></div>
                                                        <div className="flex justify-between text-lg font-bold">
                                                                <span>Out of Pocket:</span>
                                                                <span>${invoice.total.toFixed(2)}</span>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                {/* Payments */}
                                {invoice.payments && invoice.payments.length > 0 && (
                                        <div className="mb-8">
                                                <h3 className="font-semibold mb-4">Payments</h3>
                                                <div className="overflow-x-auto">
                                                        <table className="table w-full">
                                                                <thead>
                                                                        <tr>
                                                                                <th>Payment Type</th>
                                                                                <th>Amount</th>
                                                                                <th>Date</th>
                                                                                <th>Reference</th>
                                                                        </tr>
                                                                </thead>
                                                                <tbody>
                                                                        {invoice.payments.map((payment) => (
                                                                                <tr key={payment.id}>
                                                                                        <td className="capitalize">{payment.payment_type}</td>
                                                                                        <td>${payment.amount.toFixed(2)}</td>
                                                                                        <td>{payment.payment_date}</td>
                                                                                        <td>{payment.reference || '-'}</td>
                                                                                </tr>
                                                                        ))}
                                                                </tbody>
                                                        </table>
                                                </div>
                                                <div className="flex justify-end mt-4">
                                                        <div className="bg-base-200 rounded-lg p-4 w-full max-w-md space-y-2">
                                                                <div className="flex justify-between">
                                                                        <span>Amount Paid:</span>
                                                                        <span className="font-medium">${invoice.amount_paid.toFixed(2)}</span>
                                                                </div>
                                                                <div className="flex justify-between text-lg font-bold">
                                                                        <span>Balance Due:</span>
                                                                        <span className={invoice.balance > 0 ? 'text-error' : 'text-success'}>
                                                                                ${invoice.balance.toFixed(2)}
                                                                        </span>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                )}

                                {/* Notes */}
                                {invoice.notes && (
                                        <div className="mt-8">
                                                <h3 className="font-semibold mb-2">Notes</h3>
                                                <p className="text-sm opacity-70">{invoice.notes}</p>
                                        </div>
                                )}
                        </div>

                        {/* Payment Modal */}
                        {showPaymentModal && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                        <div className="bg-base-100 rounded-lg shadow-xl p-6 w-full max-w-md">
                                                <h3 className="text-lg font-semibold mb-4">Add Payment</h3>

                                                <div className="space-y-4">
                                                        <div>
                                                                <label className="label">
                                                                        <span className="label-text">Payment Type</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={paymentType}
                                                                        onChange={(e) => setPaymentType(e.target.value)}
                                                                >
                                                                        <option value="cash">Cash</option>
                                                                        <option value="eftpos">EFTPOS</option>
                                                                        <option value="bank_transfer">Bank Transfer</option>
                                                                        <option value="cheque">Cheque</option>
                                                                        <option value="credit_card">Credit Card</option>
                                                                </select>
                                                        </div>

                                                        <div>
                                                                <label className="label">
                                                                        <span className="label-text">Amount</span>
                                                                </label>
                                                                <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        className="input input-bordered w-full"
                                                                        placeholder="0.00"
                                                                        value={paymentAmount}
                                                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                                                />
                                                                <label className="label">
                                                                        <span className="label-text-alt">
                                                                                Balance Due: ${invoice.balance.toFixed(2)}
                                                                        </span>
                                                                </label>
                                                        </div>

                                                        <div>
                                                                <label className="label">
                                                                        <span className="label-text">Reference (Optional)</span>
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        className="input input-bordered w-full"
                                                                        placeholder="Payment reference"
                                                                        value={paymentReference}
                                                                        onChange={(e) => setPaymentReference(e.target.value)}
                                                                />
                                                        </div>
                                                </div>

                                                <div className="flex justify-end gap-2 mt-6">
                                                        <button onClick={() => setShowPaymentModal(false)} className="btn btn-ghost">
                                                                Cancel
                                                        </button>
                                                        <button onClick={handleAddPayment} className="btn btn-success">
                                                                Add Payment
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default InvoiceView;
